import moment from "moment";
import React, { useEffect } from "react";
import DocumentTitle from "react-document-title";
import { useHistory } from "react-router";
import Auth from "../../Auth/Auth";
import scrollToTop from "../../utils/scrollToTop";
import * as E from "../Elements/ElementStyles";
import { Separator } from "office-ui-fabric-react/lib/Separator";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { SignatureComponent } from "../../components/Signature/SignatureComponent";
import ModalElementSigned from "./ModalElementSigned";
import ElementsService, {
  SignatureContractElements,
} from "../../services/ElementsService";
import { setSavingsPlanElementsStatus } from "../../store/auth/actions";
import { useDispatch } from "react-redux";
import { ISignature } from "../../services/SignaturesService";
import TicketSupport from "../../components/TicketSupport/TicketSupport";
import { TicketSupportContainer } from "../../components/TicketSupport/TicketSupport.style";
import {
  MODAL_BODY_ERROR,
  MODAL_TITLE_ERROR,
  MODAL_TITLE_SUCCESS,
  PARTIAL_MODAL_BODY_SUCCESS,
  ScreenStepType,
} from "../../components/TicketSupport/TicketSupportConstants";
import TicketSupportService, {
  TicketSupportData,
  TicketSupportError,
} from "../../services/TicketSupportService";
import { ModalWithCancel } from "../../components/Modals/ModalWithCancel";

const ElementSignatureConfirm = (props: any) => {
  const history = useHistory();
  const activePlan = Auth.getActivePlan();
  const [modalShow, setModalShow] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [signatures, setSignatures] = React.useState<
    SignatureContractElements[]
  >([]);
  const [phoneNumbers, setPhoneNumbers] = React.useState<string>("");
  const dispatch = useDispatch();

  // ticket soporte + modal
  const [showSupportForm, setShowSupportForm] = React.useState(false);
  const [showSupportResultModal, setShowSupportResultModal] =
    React.useState(false);
  const [modalSupportResultTitle, setModalSupportResultTitle] =
    React.useState("");
  const [modalSupportResultBody, setModalSupportResultBody] =
    React.useState("");
  const [ticketError, setTicketError] = React.useState<TicketSupportError>({
    status: false,
    message: "",
  });

  React.useEffect(() => {
    scrollToTop();
  }, []);

  React.useEffect(() => {
    if (signatures.length === 0) {
      ElementsService.createElementAmount(activePlan.id)
        .then((signatures: SignatureContractElements[]) => {
          setSignatures(signatures);
          setIsLoaded(true);
        })
        .catch((error) => {
          const backendMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Ha ocurrido un error al obtener las firmas.";
          setShowSupportForm(true);
          setTicketError({
            status: true,
            message: backendMessage,
          });
        });
    }
  }, [signatures, activePlan.id, modalShow]);

  const processExpirationDate = (signs: SignatureContractElements[]) => {
    if (signs !== undefined && signs.length > 0) {
      // eslint-disable-next-line array-callback-return
      signs.map((signature: SignatureContractElements) => {
        if (signature && typeof signature.expiresAt === "string") {
          signature.expiresAt = moment(signature.expiresAt).toDate();
        }
      });
    }
  };

  useEffect(() => {
    if (signatures !== undefined) {
      setPhoneNumbers(
        signatures
          .map((signature: SignatureContractElements) =>
            signature.phone === undefined
              ? ""
              : "X".repeat(signature.phone.length - 3) +
                signature.phone.slice(
                  signature.phone.length - 3,
                  signature.phone.length
                )
          )
          .join(", ")
      );
    }
  }, [signatures]);

  const handleSubmit = async () => {
    if (signatures.length > 0 || !modalShow) {
      await dispatch(setSavingsPlanElementsStatus(activePlan.id));
      scrollToTop();
      setModalShow(true);
    }
  };

  const handleError = (error: any) => {
    const status = false;
    const message =
      error?.response?.data?.message ||
      error?.message ||
      JSON.stringify(error, Object.getOwnPropertyNames(error), 2) ||
      "Ha ocurrido un error inesperado.";

    setTicketError({ status, message });
    setShowSupportForm(true);
  };

  // -------------------------------------------------------------------
  // Ticket soporte
  // -------------------------------------------------------------------
  const handleSupportSend = async (ticketData: TicketSupportData) => {
    try {
      const response = await TicketSupportService.createNewSupportTicket(
        ticketData
      );

      if (response && response.Status === 200) {
        setModalSupportResultTitle(MODAL_TITLE_SUCCESS);
        setModalSupportResultBody(
          `Ticket con referencia: ${
            response.Id ?? "N/A"
          } ${PARTIAL_MODAL_BODY_SUCCESS}`
        );
      } else {
        setModalSupportResultTitle(MODAL_TITLE_ERROR);
        setModalSupportResultBody(MODAL_BODY_ERROR);
      }
    } catch (error) {
      console.error(`${MODAL_TITLE_ERROR} :`, error);
      setModalSupportResultTitle(MODAL_TITLE_ERROR);
      setModalSupportResultBody(MODAL_BODY_ERROR);
    } finally {
      setShowSupportResultModal(true);
    }
  };

  return (
    <>
      {/* Soporte */}
      <TicketSupportContainer>
        <TicketSupport
          showSupportForm={showSupportForm}
          setShowSupportForm={setShowSupportForm}
          onSupportSend={handleSupportSend}
          component={"ElementSignatureConfirm"}
          savingPlanId={activePlan.id}
          screenName={ScreenStepType.FirmaContratoElements}
          ticketSupportError={ticketError}
          signaturesContractElements={signatures}
        />
      </TicketSupportContainer>
      <DocumentTitle title="IronIA - Elements - Firma de Contrato">
        <React.Fragment>
          <E.HelperStyle />
          <E.ContainerSignature>
            <E.MainContainerSignature>
              <h2 style={{ margin: 0, cursor: "default" }}>
                Elements - Firma de Contrato
              </h2>
              <Separator />
              {!isLoaded && <Spinner size={SpinnerSize.large} />}
              {isLoaded && (
                <SignatureComponent
                  signatures={signatures as ISignature[]}
                  alreadySignedText=" "
                  submitButtonText="Firmar contrato"
                  cancelButtonText="Volver"
                  headerTextArea={
                    "Introduce el código que te hemos enviado" +
                    (signatures.length > 1
                      ? ` a los números ${phoneNumbers} `
                      : signatures.length === 1
                      ? ` al número ${phoneNumbers} `
                      : " ") +
                    "por SMS para firmar el contrato."
                  }
                  backButtonText="Volver"
                  onBackButtonClicked={() =>
                    history.push("/Elements/Signature")
                  }
                  onCancelButtonClicked={() =>
                    history.push("/Elements/Signature")
                  }
                  requestNewCodeButtonText="Recibir nuevo código"
                  onNewCodeSended={(signatures: any) => {
                    processExpirationDate(signatures);
                    setSignatures(signatures);
                  }}
                  onSignatureChanged={(signatures: any) => {
                    processExpirationDate(signatures);
                    setSignatures(signatures);
                  }}
                  onSigned={handleSubmit}
                  onError={handleError}
                />
              )}
            </E.MainContainerSignature>
          </E.ContainerSignature>
          {modalShow && <ModalElementSigned setModalShow={setModalShow} />}
        </React.Fragment>
      </DocumentTitle>

      {/* Modal de confirmación */}
      <ModalWithCancel
        showModal={showSupportResultModal}
        setShowModal={setShowSupportResultModal}
        title={modalSupportResultTitle}
        body={modalSupportResultBody}
        onClose={() => setShowSupportResultModal(false)}
        btnSubmitText="Cerrar"
      />
    </>
  );
};

export default ElementSignatureConfirm;
